import React, { useState, useEffect } from 'react';
import { Zap, Search, Plus, Edit, Trash2, Save, X, Calendar, Building2, CheckCircle, XCircle, MapPin, Hash, ChevronLeft, ChevronRight } from 'lucide-react';
import { buildApiUrl, API_CONFIG } from '../config/api';

interface Line {
  id: number;
  name: string;
  code?: string | null;
  company_id: number;
  company_name?: string | null;
  color?: string | null;
  type?: string | null;
  stations_count?: number | null;
  length?: string | null;
  status?: string | null;
  description?: string | null;
  created_at: string;
}

interface Company {
  id: number;
  name: string;
  company_color: string;
}

const LinesManager: React.FC = () => {
  const [lines, setLines] = useState<Line[]>([]);
  const [filteredLines, setFilteredLines] = useState<Line[]>([]);
  const [paginatedLines, setPaginatedLines] = useState<Line[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingLine, setEditingLine] = useState<Line | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newLine, setNewLine] = useState<Partial<Line>>({
    name: '',
    code: '',
    company_id: 19,
    color: '#00A651',
    type: 'Skytrain',
    length: '',
    status: 'active',
    description: '',
  });

  useEffect(() => {
    fetchLines();
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredLines(lines);
    } else {
      const filtered = lines.filter(line =>
        line.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (line.code && line.code.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (line.company_name && line.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (line.type && line.type.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredLines(filtered);
    }
  }, [lines, searchTerm]);

  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedLines(filteredLines.slice(startIndex, endIndex));
  }, [filteredLines, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchLines = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LINES));
      const data = await response.json();
      setLines(data.lines || []);
    } catch (error) {
      console.error('Error fetching lines:', error);
      setLines([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.COMPANIES));
      const data = await response.json();
      setCompanies(data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const getCompanyById = (id: number) => {
    return companies.find(company => company.id === id);
  };

  const handleCreate = async () => {
    try {
      const company = getCompanyById(newLine.company_id || 1);
      const lineData = {
        ...newLine,
        company_name: company?.name || '',
      };

      const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LINES), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lineData),
      });

      if (response.ok) {
        await fetchLines();
        setIsCreating(false);
        setNewLine({
          name: '',
          code: '',
          company_id: companies.length > 0 ? companies[0].id : 19,
          color: '#00A651',
          type: 'Skytrain',
          length: '',
          status: 'active',
          description: '',
        });
      } else {
        const errorData = await response.json();
        alert(`Error creating line: ${errorData.detail || response.statusText}`);
      }
    } catch (error) {
      console.error('Error creating line:', error);
      alert(`Error creating line: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleUpdate = async (line: Line) => {
    try {
      const company = getCompanyById(line.company_id);
      const lineData = {
        ...line,
        company_name: company?.name || '',
      };

      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.LINES}/${line.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lineData),
      });

      if (response.ok) {
        await fetchLines();
        setEditingLine(null);
      }
    } catch (error) {
      console.error('Error updating line:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this train line?')) return;

    try {
      const response = await fetch(buildApiUrl(`${API_CONFIG.ENDPOINTS.LINES}/${id}`), {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchLines();
      }
    } catch (error) {
      console.error('Error deleting line:', error);
    }
  };


  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-admin-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <Zap className="h-6 w-6 mr-2 text-admin-600" />
          Train Lines Management
        </h2>
        <button
          onClick={() => setIsCreating(true)}
          className="admin-btn-primary flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Train Line
        </button>
      </div>

      {/* Create Form */}
      {isCreating && (
        <div className="admin-card mb-6">
          <h3 className="text-lg font-semibold mb-4">Create New Train Line</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Line Name</label>
              <input
                type="text"
                placeholder="e.g., BTS Sukhumvit Line"
                value={newLine.name}
                onChange={(e) => setNewLine({ ...newLine, name: e.target.value })}
                className="admin-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Line Code</label>
              <input
                type="text"
                placeholder="e.g., SUK"
                value={newLine.code || ''}
                onChange={(e) => setNewLine({ ...newLine, code: e.target.value.toUpperCase() })}
                className="admin-input"
                maxLength={5}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Train Company</label>
              <select
                value={newLine.company_id}
                onChange={(e) => setNewLine({ ...newLine, company_id: parseInt(e.target.value) })}
                className="admin-input"
              >
                {companies.map((company) => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Line Color</label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={newLine.color || '#00A651'}
                  onChange={(e) => setNewLine({ ...newLine, color: e.target.value })}
                  className="w-12 h-10 rounded border border-gray-300"
                />
                <input
                  type="text"
                  value={newLine.color || '#00A651'}
                  onChange={(e) => setNewLine({ ...newLine, color: e.target.value })}
                  className="admin-input flex-1"
                  placeholder="#00A651"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Train Type</label>
              <select
                value={newLine.type || 'Skytrain'}
                onChange={(e) => setNewLine({ ...newLine, type: e.target.value })}
                className="admin-input"
              >
                <option value="Skytrain">Skytrain</option>
                <option value="Subway">Subway</option>
                <option value="Elevated">Elevated</option>
                <option value="Express Rail">Express Rail</option>
                <option value="Monorail">Monorail</option>
                <option value="Bus Rapid Transit">Bus Rapid Transit</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Line Length</label>
              <input
                type="text"
                placeholder="e.g., 30.4 km"
                value={newLine.length || ''}
                onChange={(e) => setNewLine({ ...newLine, length: e.target.value })}
                className="admin-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select
                value={newLine.status || 'active'}
                onChange={(e) => setNewLine({ ...newLine, status: e.target.value })}
                className="admin-input"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div className="md:col-span-2 lg:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                placeholder="Line description and route information"
                value={newLine.description || ''}
                onChange={(e) => setNewLine({ ...newLine, description: e.target.value })}
                className="admin-input"
                rows={3}
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={handleCreate} className="btn-primary flex items-center">
              <Save className="h-4 w-4 mr-2" />
              Save Line
            </button>
            <button
              onClick={() => {
                setIsCreating(false);
                setNewLine({
                  name: '',
                  code: '',
                  company_id: 5,
                  color: '#00A651',
                  type: 'Skytrain',
                  length: '',
                  status: 'active',
                  description: '',
                });
              }}
              className="btn-secondary flex items-center"
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="admin-card mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search lines by name, code, company, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="admin-input pl-10"
          />
        </div>
      </div>

      {/* Lines Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {paginatedLines.map((line) => (
          <div key={line.id} className="admin-card">
            {editingLine?.id === line.id ? (
              // Edit Mode
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Line Name</label>
                  <input
                    type="text"
                    value={editingLine.name || ''}
                    onChange={(e) => setEditingLine({ ...editingLine, name: e.target.value })}
                    className="admin-input text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                    <input
                      type="text"
                      value={editingLine.code || ''}
                      onChange={(e) => setEditingLine({ ...editingLine, code: e.target.value.toUpperCase() })}
                      className="admin-input text-sm"
                      maxLength={5}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                    <input
                      type="color"
                      value={editingLine.color || '#666666'}
                      onChange={(e) => setEditingLine({ ...editingLine, color: e.target.value })}
                      className="admin-input text-sm h-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                  <select
                    value={editingLine.company_id}
                    onChange={(e) => {
                      const companyId = parseInt(e.target.value);
                      const company = getCompanyById(companyId);
                      setEditingLine({
                        ...editingLine,
                        company_id: companyId,
                        company_name: company?.name || ''
                      });
                    }}
                    className="admin-input text-sm"
                  >
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>{company.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={editingLine.type || 'Skytrain'}
                      onChange={(e) => setEditingLine({ ...editingLine, type: e.target.value })}
                      className="admin-input text-sm"
                    >
                      <option value="Skytrain">Skytrain</option>
                      <option value="Subway">Subway</option>
                      <option value="Elevated">Elevated</option>
                      <option value="Express Rail">Express Rail</option>
                      <option value="Monorail">Monorail</option>
                      <option value="Bus Rapid Transit">Bus Rapid Transit</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={editingLine.status || 'active'}
                      onChange={(e) => setEditingLine({ ...editingLine, status: e.target.value })}
                      className="admin-input text-sm"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Length</label>
                  <input
                    type="text"
                    value={editingLine.length || ''}
                    onChange={(e) => setEditingLine({ ...editingLine, length: e.target.value })}
                    className="admin-input text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editingLine.description || ''}
                    onChange={(e) => setEditingLine({ ...editingLine, description: e.target.value })}
                    className="admin-input text-sm"
                    rows={2}
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => handleUpdate(editingLine)}
                    className="text-green-600 hover:text-green-900 p-2"
                  >
                    <Save className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingLine(null)}
                    className="text-gray-600 hover:text-gray-900 p-2"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <div>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div
                      className="w-6 h-6 rounded mr-3 border-2 border-white shadow-lg"
                      style={{ backgroundColor: line.color || '#666' }}
                    ></div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                        {line.name}
                        {line.code && (
                          <span
                            className="ml-2 px-2 py-1 text-xs font-bold text-white rounded"
                            style={{ backgroundColor: line.color || '#666' }}
                          >
                            {line.code}
                          </span>
                        )}
                      </h3>
                      <div className="flex items-center mt-1 space-x-2">
                        {line.type && (
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {line.type}
                          </span>
                        )}
                        {line.status === 'active' ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : line.status === 'inactive' ? (
                          <XCircle className="h-4 w-4 text-red-500" />
                        ) : null}
                        <span className={`text-xs font-medium px-2 py-1 rounded ${
                          line.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {line.status ? line.status.charAt(0).toUpperCase() + line.status.slice(1) : 'Unknown'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingLine(line)}
                      className="text-admin-600 hover:text-admin-900 p-1"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(line.id)}
                      className="text-red-600 hover:text-red-900 p-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  {line.description && (
                    <p className="text-sm text-gray-600">{line.description}</p>
                  )}

                  {line.company_name && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Building2 className="h-4 w-4 mr-2 text-gray-400" />
                      {line.company_name}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                    {line.stations_count && (
                      <div className="flex items-center">
                        <MapPin className="h-4 w-4 mr-1 text-gray-400" />
                        {line.stations_count} stations
                      </div>
                    )}
                    {line.length && (
                      <div className="flex items-center">
                        <Hash className="h-4 w-4 mr-1 text-gray-400" />
                        {line.length}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center text-xs text-gray-500 pt-2 border-t">
                    <Calendar className="h-4 w-4 mr-1" />
                    Created: {new Date(line.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination Controls */}
      {filteredLines.length > 0 && (
        <div className="flex items-center justify-between px-6 py-3 bg-white border-t border-gray-200 mt-6 rounded-lg">
          <div className="flex items-center text-sm text-gray-700">
            <span>
              Showing {Math.min((currentPage - 1) * itemsPerPage + 1, filteredLines.length)} to{' '}
              {Math.min(currentPage * itemsPerPage, filteredLines.length)} of {filteredLines.length} results
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.ceil(filteredLines.length / itemsPerPage) }, (_, i) => i + 1)
                .filter(page =>
                  page === 1 ||
                  page === Math.ceil(filteredLines.length / itemsPerPage) ||
                  (page >= currentPage - 2 && page <= currentPage + 2)
                )
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="px-2 text-gray-400">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        currentPage === page
                          ? 'bg-admin-600 text-white'
                          : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))
              }
            </div>
            <button
              onClick={() => setCurrentPage(Math.min(Math.ceil(filteredLines.length / itemsPerPage), currentPage + 1))}
              disabled={currentPage === Math.ceil(filteredLines.length / itemsPerPage)}
              className="flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
      )}

      {filteredLines.length === 0 && !loading && (
        <div className="admin-card text-center py-8">
          <Zap className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No train lines found.</p>
        </div>
      )}
    </div>
  );
};

export default LinesManager;